import {styled} from '@mui/material/styles';
import {Box, Paper} from '@mui/material';
import {isInIframe} from '../../../../utils/iframeUtils';
import {createGradientFromColor, getContrastTextColor} from 'packages/common/utils/colorUtils';

/**
 * @file Composants stylés partagés par les vues de widgets (conteneur, en-tête, pied, zone défilable).
 * @module widgets.common.components.styled.WidgetViewStyles
 *
 * Les styles s'adaptent au contexte d'intégration : `embedded` (iframe) supprime
 * les marges/ombres et fait occuper toute la surface hôte, tandis que le mode
 * autonome conserve une carte centrée à largeur/hauteur bornées. Le `displayMode`
 * (`fixed` vs `floating`) ajuste le comportement plein écran sur mobile.
 */

// Conteneur de l'état de chargement
export const LoadingContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  minHeight: 400,
  padding: theme.spacing(3),
}));

export const WidgetContainer = styled(Paper, {
  shouldForwardProp: (prop) => !['embedded', 'displayMode', 'maxWidth', 'maxHeight', 'widgetType'].includes(prop),
})(({
  theme,
  embedded = isInIframe(),
  displayMode = 'fixed',
  maxWidth = '700px',
  maxHeight = '650px',
  widgetType = 'reservations',
}) => ({
  width: '100%',
  maxWidth: embedded ? '100%' : maxWidth,
  margin: embedded ? '0' : '0 auto',
  height: embedded && displayMode === 'floating' ? 'auto' : embedded ? '100%' : 'auto',
  maxHeight: embedded ? 'none' : maxHeight,
  minHeight: embedded && displayMode === 'floating' ? '400px' : 'auto',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: embedded ? 0 : 12,
  boxShadow: embedded ?
    'none' :
    (widgetType === 'reservations' ? theme.shadows[10] : 'none'),
  backgroundColor: widgetType === 'giftvouchers' ? 'transparent' : theme.palette.background.paper,
  position: 'relative',
  overflow: 'hidden',
  transition: embedded ? 'none' : 'height 0.3s ease',

  // Optimisations mobile pour mode floating
  ...(embedded && displayMode === 'floating' && {
    '@media (max-width: 480px)': {
      // Support multi-navigateur pour la hauteur complète (évite le débordement sous les barres système)
      height: '100%',
      height: '-moz-available',
      height: '-webkit-fill-available',
      height: 'stretch',
      maxHeight: '100%',
      maxHeight: '-moz-available',
      maxHeight: '-webkit-fill-available',
      maxHeight: 'stretch',
      width: '100vw',
      borderRadius: 0,
      backgroundColor: theme.palette.background.paper,
    },
  }),
}));

export const WidgetHeader = styled(Box, {
  shouldForwardProp: (prop) => !['variant', 'primaryColor'].includes(prop),
})(({theme, variant = 'default', primaryColor}) => {
  // Calculer la couleur de contraste pour le texte
  const contrastTextColor = primaryColor ?
    getContrastTextColor(primaryColor) :
    theme.palette.primary.contrastText;

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    ...(variant === 'default' && {
      background: primaryColor && primaryColor.startsWith('#') ?
        createGradientFromColor(primaryColor, {
          darkenAmount: 20,
          angle: 45,
          startPosition: 30,
          endPosition: 90,
          useDarkerStart: true,
        }) :
        `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
      color: contrastTextColor,
    }),
    ...(variant === 'warning' && {
      background: `linear-gradient(45deg, ${theme.palette.warning.dark} 30%, ${theme.palette.warning.main} 90%)`,
      color: theme.palette.warning.contrastText,
    }),
    ...(variant === 'error' && {
      background: `linear-gradient(45deg, ${theme.palette.error.dark} 30%, ${theme.palette.error.main} 90%)`,
      color: theme.palette.error.contrastText,
    }),
  };
});

export const FixedHeaderContainer = styled(Box, {
  shouldForwardProp: (prop) => !['embedded', 'displayMode'].includes(prop),
})(({theme, embedded = isInIframe(), displayMode = 'fixed'}) => ({
  position: embedded ? 'fixed' : 'sticky',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 9999,
  backgroundColor: 'background.paper',
  borderBottom: `1px solid ${theme.palette.divider}`,
  width: '100%',
  margin: embedded ? '0' : '0 auto',
}));

export const HeaderSpacerBox = styled(Box, {
  shouldForwardProp: (prop) => !['hasSteps', 'embedded'].includes(prop),
})(({hasSteps = true, embedded = isInIframe()}) => ({
  height: !embedded ? 0 : (hasSteps ? '132px' : '64px'),
  flexShrink: 0,
  display: embedded ? 'block' : 'none',
}));

export const ScrollableContentBox = styled(Box, {
  shouldForwardProp: (prop) => !['hasFooter', 'embedded', 'displayMode'].includes(prop),
})(({theme, hasFooter = true, embedded = isInIframe(), displayMode = 'fixed'}) => {
  // En mode flottant embedded, l'iframe occupe 100% de l'écran sur mobile : le scroll
  // doit se faire à l'intérieur de ce conteneur (le WidgetContainer étant height: 100%).
  // En mode fixe embedded sans footer, l'iframe se redimensionne au contenu via le
  // système de messages : overflow visible laisse le contenu déborder pour la mesure.
  const isFloatingEmbedded = embedded && displayMode === 'floating';

  return {
    flexGrow: 1,
    minHeight: 0,
    overflowY: (embedded && !hasFooter && !isFloatingEmbedded) ? 'visible' : 'auto',
    WebkitOverflowScrolling: 'touch',
    padding: theme.spacing(2),
    // Padding ajusté pour le footer avec branding (StepActions + BrandingFooter ~120px)
    paddingBottom: (hasFooter && embedded) ? theme.spacing(15) : 0,
  };
});

export const FixedFooterContainer = styled(Box, {
  shouldForwardProp: (prop) => !['embedded', 'displayMode'].includes(prop),
})(({theme, embedded = isInIframe(), displayMode = 'fixed'}) => ({
  position: embedded ? 'fixed' : 'sticky',
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  zIndex: 9999,
  width: embedded ? '100%' : 'inherit',
  maxWidth: embedded ? '100%' : '600px',
  margin: embedded ? '0' : '0 auto',
}));

export const ContentContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'embedded' && prop !== 'displayMode',
})(({theme, embedded = isInIframe(), displayMode = 'fixed'}) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  minHeight: embedded && displayMode === 'floating' ? '400px' : 'auto',
  ...(!embedded && {
    overflow: 'auto',
  }),
}));
